import React, { useMemo, useState } from 'react';
import { Integrator } from '../types';

export const integratorLabel = (i: Integrator) => (i.nip ? `${i.name} — NIP ${i.nip}` : i.name);

/**
 * Wspólny mechanizm wskazywania firmy dla widoków super-admina: input z dropdownem
 * autouzupełniania po NIP-ie (prefiks) lub nazwie (fragment) — ten sam co w zakładce
 * „Integratorzy". Wpisanie tekstu kasuje wybór; przycisk × czyści filtr.
 */
const IntegratorAutocomplete: React.FC<{
  integrators: Integrator[];
  value: Integrator | null;
  onChange: (i: Integrator | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  style?: React.CSSProperties;
}> = ({ integrators, value, onChange, label, placeholder, required, style }) => {
  const [query, setQuery] = useState(value ? integratorLabel(value) : '');
  const [open, setOpen] = useState(false);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? integrators.filter((i) =>
          (i.nip || '').toLowerCase().startsWith(q) || i.name.toLowerCase().includes(q))
      : integrators;
    return pool.slice(0, 8);
  }, [query, integrators]);

  const pick = (i: Integrator) => {
    onChange(i);
    setQuery(integratorLabel(i));
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      {label && <label className="form-label">{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          className="form-input"
          value={query}
          placeholder={placeholder ?? 'np. 1234567890 albo Serwis Kowalski…'}
          onChange={(e) => { setQuery(e.target.value); onChange(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          required={required}
          style={query ? { paddingRight: '2rem' } : undefined}
        />
        {query && (
          <button
            type="button"
            aria-label="Wyczyść"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
            style={{
              position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: '1rem', lineHeight: 1, color: 'var(--color-text-muted)', padding: '0.25rem',
            }}
          >
            ×
          </button>
        )}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
          background: 'var(--color-surface)', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: 260, overflowY: 'auto',
        }}>
          {suggestions.map((i) => (
            <button
              type="button"
              key={i.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(i)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '0.5rem 0.75rem', border: 'none', background: 'transparent',
                cursor: 'pointer', fontSize: '0.875rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-alt)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {i.name} <span className="mono">{i.nip ? `— NIP ${i.nip}` : '— brak NIP'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default IntegratorAutocomplete;
